import { useCallback, useState, useEffect } from 'react';
import { useDataAccess } from './useDataAccess';
import { useCustomer } from '../contexts/CustomerContext';
import { fetchEquipmentWithPendingChanges, fetchPendingCreations, fetchPendingChanges } from '../api/equipmentApi';

/**
 * Custom hook for managing equipment inventory data
 * 
 * This hook provides utilities for:
 * - Fetching equipment data from the new wards endpoints
 * - Managing loading states, error handling, and data caching
 * - Handling the hierarchical equipment data structure
 * - Restricting access to 'wards' customer only
 */
export const useEquipmentData = () => {
  const [equipmentData, setEquipmentData] = useState(null);
  const [tablesData, setTablesData] = useState(null);
  const [pendingCreations, setPendingCreations] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { makeCustomerApiRequest } = useDataAccess();
  const { currentCustomer, getEffectiveCustomerId } = useCustomer();

  // Check if current customer is 'wards'
  const isWardsCustomer = useCallback(() => {
    const customerId = getEffectiveCustomerId();
    return customerId === 'wards';
  }, [getEffectiveCustomerId]);

  // Fetch equipment data from the endpoint
  const fetchEquipmentData = useCallback(async () => {
    if (!isWardsCustomer()) {
      setError(new Error('Equipment inventory is only available for Wards customers'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // console.log('🔍 useEquipmentData: Fetching equipment data for Wards customer');
      
      // Try the enhanced API first (with pending changes support)
      let response;
      try {
        response = await fetchEquipmentWithPendingChanges('wards');
        // console.log('✅ useEquipmentData: Equipment API response with pending changes:', response);
      } catch (enhancedApiError) {
        // console.log('🔄 useEquipmentData: Enhanced API not available, using original equipment API');
        response = await makeCustomerApiRequest('equipment', {}, 'wards');
        // console.log('✅ useEquipmentData: Original equipment API response:', response);
      }

      if (response && response.success && response.data) {
        setEquipmentData(response.data);
      } else {
        throw new Error('Invalid equipment data response');
      }
    } catch (error) {
      console.error('useEquipmentData: Failed to fetch equipment data:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [isWardsCustomer, makeCustomerApiRequest]);

  // Fetch tables data from the new endpoint
  const fetchTablesData = useCallback(async () => {
    if (!isWardsCustomer()) {
      setError(new Error('Tables data is only available for Wards customers'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // console.log('🔍 useEquipmentData: Fetching tables data for Wards customer');
      
      const response = await makeCustomerApiRequest('tables', {}, 'wards');
      // console.log('✅ useEquipmentData: Tables API response:', response);

      if (response && response.success && response.data) {
        setTablesData(response.data);
      } else {
        throw new Error('Invalid tables data response');
      }
    } catch (error) {
      console.error('useEquipmentData: Failed to fetch tables data:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [isWardsCustomer, makeCustomerApiRequest]);

  // Fetch pending creations data
  const fetchPendingCreationsData = useCallback(async () => {
    if (!isWardsCustomer()) {
      return;
    }

    try {
      // console.log('🔍 useEquipmentData: Fetching pending creations for Wards customer');
      
      const response = await fetchPendingCreations('wards');
      // console.log('✅ useEquipmentData: Pending creations response:', response);

      // Handle different response structures
      let creationsData = [];
      if (response) {
        if (Array.isArray(response)) {
          creationsData = response;
        } else if (response.data && Array.isArray(response.data)) {
          creationsData = response.data;
        } else if (response.changes && Array.isArray(response.changes)) {
          creationsData = response.changes;
        }
      }
      
      setPendingCreations(creationsData);
    } catch (error) {
      console.error('useEquipmentData: Failed to fetch pending creations:', error);
      setPendingCreations([]);
    }
  }, [isWardsCustomer]);

  // Fetch all pending changes (including modifications)
  const fetchPendingChangesData = useCallback(async () => {
    if (!isWardsCustomer()) {
      return;
    }

    try {
      // console.log('🔍 useEquipmentData: Fetching all pending changes for Wards customer');
      
      const response = await fetchPendingChanges('wards');
      // console.log('✅ useEquipmentData: Pending changes response:', response);

      // Handle different response structures
      let allChanges = [];
      if (response) {
        if (response.data && response.data.all && Array.isArray(response.data.all)) {
          allChanges = response.data.all;
        } else if (Array.isArray(response)) {
          allChanges = response;
        } else if (response.data && Array.isArray(response.data)) {
          allChanges = response.data;
        } else if (response.changes && Array.isArray(response.changes)) {
          allChanges = response.changes;
        }
      }
      
      // Filter out policy changes - those are handled separately
      allChanges = allChanges.filter(change => change.changeType !== 'policy');
      
      setPendingChanges(allChanges);
    } catch (error) {
      console.error('useEquipmentData: Failed to fetch pending changes:', error);
      setPendingChanges([]);
    }
  }, [isWardsCustomer]);

  // Refresh all equipment data
  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchEquipmentData(),
      fetchTablesData(),
      fetchPendingCreationsData(),
      fetchPendingChangesData()
    ]);
  }, [fetchEquipmentData, fetchTablesData, fetchPendingCreationsData, fetchPendingChangesData]);

  // Auto-fetch data when customer changes to 'wards'
  useEffect(() => {
    if (isWardsCustomer()) {
      refreshData();
    } else {
      // Clear data if not Wards customer
      setEquipmentData(null);
      setTablesData(null);
      setPendingCreations([]);
      setPendingChanges([]);
      setError(null);
    }
  }, [isWardsCustomer, refreshData]);

  // Get current access context for debugging
  const getAccessContext = useCallback(() => {
    return {
      customer: {
        current: currentCustomer,
        effectiveId: getEffectiveCustomerId(),
        isWards: isWardsCustomer()
      },
      data: {
        equipment: equipmentData,
        tables: tablesData,
        pendingCreations,
        pendingChanges,
        loading,
        error
      }
    };
  }, [currentCustomer, getEffectiveCustomerId, isWardsCustomer, equipmentData, tablesData, loading, error]);

  return {
    // Data
    equipmentData,
    tablesData,
    pendingCreations,
    pendingChanges,
    
    // State
    loading,
    error,
    
    // Actions
    fetchEquipmentData,
    fetchTablesData,
    fetchPendingCreationsData,
    fetchPendingChangesData,
    refreshData,
    
    // Access control
    isWardsCustomer,
    
    // Context and debugging
    getAccessContext
  };
};

export default useEquipmentData;
